// Foundry - Business Plan Generator Types

// ============================================================================
// Enums (matching database)
// ============================================================================

export type PlanStatus =
  | 'draft'
  | 'canvas_complete'
  | 'in_progress'
  | 'completed'
  | 'archived';

export type SectionStatus =
  | 'processing'  // AI is generating content
  | 'empty'
  | 'ai_draft'
  | 'in_progress'
  | 'approved'
  | 'failed';     // AI generation failed

// ============================================================================
// Lean Canvas
// ============================================================================

export interface LeanCanvas {
  problem: CanvasProblem | null;
  customer_segments: CanvasCustomerSegments | null;
  unique_value_proposition: CanvasUVP | null;
  solution: CanvasSolution | null;
  channels: CanvasChannels | null;
  revenue_streams: CanvasRevenueStreams | null;
  cost_structure: CanvasCostStructure | null;
  key_metrics: CanvasKeyMetrics | null;
  unfair_advantage: CanvasUnfairAdvantage | null;
}

export interface CanvasProblem {
  primary: string;
  secondary: string[];
  existing_alternatives: string[];
}

export interface CanvasCustomerSegments {
  primary: string;
  secondary: string[];
  early_adopters: string;
}

export interface CanvasUVP {
  headline: string;
  subheadline: string;
  high_level_concept: string;
}

export interface CanvasSolution {
  primary_features: string[];
  mvp_scope: string;
  differentiators: string[];
}

export interface CanvasChannels {
  awareness: string[];
  acquisition: string[];
  retention: string[];
}

export interface CanvasRevenueStreams {
  primary_model: string;
  pricing_strategy: string;
  revenue_targets: {
    year1: number;
    year3: number;
    year5: number;
  };
}

export interface CanvasCostStructure {
  fixed_costs: string[];
  variable_costs: string[];
  key_investments: string[];
}

export interface CanvasKeyMetrics {
  north_star: string;
  leading_indicators: string[];
  lagging_indicators: string[];
}

export interface CanvasUnfairAdvantage {
  primary: string;
  moat_type: 'network_effects' | 'switching_costs' | 'brand' | 'proprietary_tech' | 'data' | 'other';
  defensibility_score: number;
}

// ============================================================================
// Idea Capture (Brain Dump Workflow)
// ============================================================================

export interface RawIdea {
  id: string;
  content: string;
  created_at: string;
  tags?: string[];
}

export type IdeaThemeType =
  | 'problem'
  | 'solution'
  | 'target_customer'
  | 'market'
  | 'revenue'
  | 'competition'
  | 'advantage'
  | 'resource'
  | 'risk'
  | 'other';

export interface IdeaTheme {
  type: IdeaThemeType;
  label: string;
  ideas: string[];
  confidence: number;
  insights?: string;
}

export interface IdentifiedGap {
  area: string;
  description: string;
  importance: 'critical' | 'important' | 'nice_to_have';
  suggested_questions: string[];
}

export interface OrganizedIdeas {
  themes: IdeaTheme[];
  gaps: IdentifiedGap[];
  patterns: string[];
  connections: Array<{ from: string; to: string; relationship: string }>;
  summary: string;
}

export interface GuidedQuestion {
  id: string;
  question: string;
  context: string;
  area: IdeaThemeType;
  importance: 'critical' | 'important' | 'nice_to_have';
  answer?: string;
  follow_ups?: string[];
}

export interface IdeaCaptureState {
  raw_ideas: RawIdea[];
  organized?: OrganizedIdeas;
  questions: GuidedQuestion[];
  answers: Record<string, string>;
  stage: 'braindump' | 'organizing' | 'questions' | 'ready';
}

// ============================================================================
// User Context
// ============================================================================

export interface UserContext {
  existing_business: string | null;
  available_resources: string | null;
  market_research: string | null;
  team_background: string | null;
  funding_status: string | null;
  idea_capture?: IdeaCaptureState;
}

// ============================================================================
// Business Plan
// ============================================================================

export interface BusinessPlan {
  id: string;
  user_id: string;
  name: string;
  description: string | null;
  industry: string | null;
  business_idea: string | null;
  status: PlanStatus;
  lean_canvas: LeanCanvas;
  user_context: UserContext;
  created_at: string;
  updated_at: string;
}

// ============================================================================
// Sections
// ============================================================================

export const SECTION_KEYS = [
  'executive_summary',
  'market_analysis',
  'financial_projections',
  'go_to_market',
  'execution_plan',
] as const;

export type SectionKey = typeof SECTION_KEYS[number];

export const SECTION_CONFIG: Record<SectionKey, { title: string; order: number }> = {
  executive_summary: { title: 'Executive Summary', order: 1 },
  market_analysis: { title: 'Market Analysis', order: 2 },
  financial_projections: { title: 'Financial Projections', order: 3 },
  go_to_market: { title: 'Go-to-Market Strategy', order: 4 },
  execution_plan: { title: 'Execution Plan & Timeline', order: 5 },
};

export interface PlanSection {
  id: string;
  plan_id: string;
  section_key: SectionKey;
  section_title: string;
  section_order: number;
  content_md: string | null;
  content_data: SectionContentData;
  sanity_checks: SanityCheck[];
  status: SectionStatus;
  created_at: string;
  updated_at: string;
}

export interface SectionContentData {
  charts?: ChartData[];
  financial?: FinancialChartData;
  market?: MarketChartData;
  execution_plan?: ExecutionPlanChartData;
  tables?: TableData[];
  metrics?: MetricData[];
  // Cached AI-generated refinement questions
  refinement_questions?: CachedRefinementQuestions;
  // User's saved answers to refinement questions
  refinement_answers?: SavedRefinementAnswers;
  // Follow-up questions generated from answers
  follow_up_questions?: FollowUpQuestion[];
  // Version history for regenerations
  version_history?: SectionVersionHistory;
}

// AI-Generated Refinement Questions (cached per section)
export interface RefinementQuestion {
  id: string;
  question: string;
  context: string;                // Why this question matters
  priority: 'high' | 'medium' | 'low';
  category: 'clarification' | 'challenge' | 'expansion' | 'validation';
}

export interface CachedRefinementQuestions {
  questions: RefinementQuestion[];
  content_hash: string;           // Hash of content_md when questions were generated
  generated_at: string;           // ISO timestamp
}

// User's answers to refinement questions (persisted)
export interface RefinementAnswer {
  id: string;                     // Unique ID for the answer
  question_id: string;            // Links to RefinementQuestion.id
  question_text: string;          // The question text (stored for reference even if questions regenerate)
  answer: string;                 // User's answer
  created_at: string;             // ISO timestamp
  updated_at: string;             // ISO timestamp
}

export interface SavedRefinementAnswers {
  answers: RefinementAnswer[];
  last_updated: string;           // ISO timestamp of most recent change
}

// Follow-up questions generated after answering a question
export interface FollowUpQuestion {
  id: string;
  parent_answer_id: string;       // Links to the answer that triggered this
  question: string;
  context: string;
  priority: 'high' | 'medium' | 'low';
}

// Version history for section regenerations
export interface SectionVersion {
  id: string;
  version_number: number;
  content_md: string;
  content_data: SectionContentData;
  answers_used: RefinementAnswer[];   // Answers that were incorporated
  generated_at: string;               // ISO timestamp
  trigger: 'initial' | 'refinement';  // What triggered this version
}

export interface SectionVersionHistory {
  versions: SectionVersion[];
  current_version_id: string;
}

// Financial-specific chart data (for Financial Projections section)
export interface FinancialChartData {
  years: number[];               // [1, 2, 3, 4, 5]
  revenue: number[];             // Projected revenue by year
  costs: number[];               // Projected costs by year
  profit: number[];              // Calculated profit by year
  growth_rates?: number[];       // YoY growth percentages
  break_even_year?: number;      // When profit turns positive
  currency?: string;             // e.g., "USD", "AUD"
}

// Market-specific chart data (for Market Analysis section)
export interface MarketChartData {
  tam: number;                   // Total Addressable Market
  sam: number;                   // Serviceable Addressable Market
  som: number;                   // Serviceable Obtainable Market
  tam_label?: string;            // e.g., "Global SaaS market"
  sam_label?: string;            // e.g., "Enterprise B2B SaaS"
  som_label?: string;            // e.g., "Mid-market companies"
  currency?: string;             // e.g., "USD", "AUD"
}

// Execution Plan types (for Execution Plan section)
export type ResourceRole = 'engineering' | 'product' | 'design' | 'marketing' | 'sales' | 'operations';
export type MilestoneCategory = 'product' | 'marketing' | 'operations' | 'funding' | 'team';
export type MilestonePriority = 'critical' | 'high' | 'medium' | 'low';
export type AssumptionImpact = 'high' | 'medium' | 'low';

export interface ExecutionMilestone {
  id: string;
  name: string;
  description: string;
  start_month: number;           // Month 1-60 (5 years)
  duration_months: number;
  category: MilestoneCategory;
  priority: MilestonePriority;
  dependencies?: string[];       // IDs of prerequisite milestones
  assumptions?: string[];        // Key assumptions for this milestone
  resources_required: Partial<Record<ResourceRole, number>>;  // FTE months per role
}

export interface ExecutionAssumption {
  id: string;
  text: string;
  impact: AssumptionImpact;
  affected_milestones: string[];
  validation_method?: string;
}

export interface ResourceAllocation {
  role: ResourceRole;
  headcount: number;             // Current team size
  monthly_cost: number;          // Cost per FTE
  ramp_plan?: Array<{ month: number; headcount: number }>;
}

export interface FundingEvent {
  month: number;
  amount: number;
  type: string;                  // e.g., "Seed", "Series A"
}

export interface ExecutionPlanChartData {
  milestones: ExecutionMilestone[];
  assumptions: ExecutionAssumption[];
  resources: ResourceAllocation[];

  // Financial projections
  initial_runway_months: number;
  monthly_burn_rate: number;
  available_capital: number;
  funding_events?: FundingEvent[];

  // Calculated values (updated when sliders change)
  critical_path_months: number;
  total_investment_required: number;
  break_even_month?: number;
  currency?: string;
}

export interface ChartData {
  type: 'line' | 'bar' | 'area' | 'pie';
  title: string;
  data: Record<string, unknown>[];
  config: {
    xAxis?: string;
    yAxis?: string;
    series?: Array<{ key: string; name: string; color?: string }>;
  };
}

export interface TableData {
  title: string;
  headers: string[];
  rows: string[][];
}

export interface MetricData {
  label: string;
  value: string;
  benchmark?: string;
  status: 'good' | 'warning' | 'concern';
}

// ============================================================================
// Sanity Checks
// ============================================================================

export interface SanityCheck {
  check: string;
  passed: boolean;
  message: string;
  severity: 'info' | 'warning' | 'error' | 'critical';
  recommendation?: string;
  benchmark?: string;
}

export interface SanityCheckResult {
  overall_score: number;
  risk_level: 'low' | 'medium' | 'high' | 'critical';
  checks: SanityCheck[];
  critical_issues: string[];
  improvements: string[];
  strengths: string[];
}

// ============================================================================
// API Request/Response Types
// ============================================================================

export interface CreatePlanRequest {
  name: string;
  description?: string;
  industry?: string;
  business_idea?: string;
  user_context?: Partial<UserContext>;
}

export interface UpdatePlanRequest {
  name?: string;
  description?: string;
  industry?: string;
  business_idea?: string;
  lean_canvas?: Partial<LeanCanvas>;
  user_context?: Partial<UserContext>;
  status?: PlanStatus;
}

export interface GenerateCanvasRequest {
  business_idea: string;
  user_context: Partial<UserContext>;
}

export interface GenerateCanvasResponse {
  lean_canvas: LeanCanvas;
  confidence: number;
  suggestions: string[];
  missing_info: string[];
}

export interface ExpandSectionRequest {
  section_key: SectionKey;
  additional_instructions?: string;
  answers_used?: RefinementAnswer[];   // Answers being incorporated in this regeneration
}

export interface ExpandSectionResponse {
  content_md: string;
  content_data: SectionContentData;
  sanity_checks: SanityCheck[];
  suggestions: string[];
}

export interface SanityCheckResponse {
  result: SanityCheckResult;
}

// Refinement Questions API Types
export interface GenerateRefinementQuestionsRequest {
  section_key: SectionKey;
}

export interface GenerateRefinementQuestionsResponse {
  questions: RefinementQuestion[];
  content_hash: string;
  generated_at: string;
}

// Idea Capture API Types
export interface OrganizeIdeasRequest {
  raw_ideas: RawIdea[];
  user_context?: Partial<UserContext>;
}

export interface OrganizeIdeasResponse {
  organized: OrganizedIdeas;
  suggested_questions: GuidedQuestion[];
}

export interface GenerateQuestionsRequest {
  organized: OrganizedIdeas;
  existing_answers?: Record<string, string>;
}

export interface GenerateQuestionsResponse {
  questions: GuidedQuestion[];
  ready_for_canvas: boolean;
  coverage_score: number;
}

export interface AnswerQuestionRequest {
  question_id: string;
  answer: string;
}

export interface AnswerQuestionResponse {
  follow_up_questions?: GuidedQuestion[];
  updated_themes?: IdeaTheme[];
}

// ============================================================================
// Full Plan with Sections (for display)
// ============================================================================

export interface BusinessPlanWithSections extends BusinessPlan {
  sections: PlanSection[];
}

// ============================================================================
// User Profile (Cross-Plan Context)
// ============================================================================

export interface UserProfilePreferences {
  communication_style: 'concise' | 'detailed' | 'conversational' | null;
  risk_tolerance: 'conservative' | 'balanced' | 'aggressive' | null;
  detail_level: 'high_level' | 'moderate' | 'granular' | null;
  focus_areas: string[];
}

export interface UserProfileExpertise {
  industries: string[];
  business_models: string[];
  target_markets: ('b2b' | 'b2c' | 'b2b2c' | 'marketplace')[];
  technical_background: 'non_technical' | 'technical' | 'highly_technical' | null;
}

export interface UserProfilePatterns {
  common_strengths: string[];
  recurring_gaps: string[];
  typical_assumptions: string[];
  decision_tendencies: string[];
}

export interface UserProfileHistory {
  plans_completed: number;
  plans_abandoned: number;
  total_ideas_captured: number;
  favorite_sections: SectionKey[];
  last_industries: string[];
}

export interface UserProfileSettings {
  challenge_level: 'gentle' | 'balanced' | 'aggressive';
  ai_tone: 'professional' | 'casual' | 'direct';
  show_benchmarks: boolean;
  auto_save: boolean;
}

export interface UserLearning {
  plan_id: string;
  plan_name: string;
  extracted_at: string;
  industry: string | null;
  key_insights: string[];
  strengths_shown: string[];
  gaps_identified: string[];
  assumptions_made: string[];
  outcome: 'completed' | 'abandoned' | 'in_progress';
}

export interface UserProfile {
  id: string;
  user_id: string;
  preferences: UserProfilePreferences;
  expertise: UserProfileExpertise;
  patterns: UserProfilePatterns;
  history: UserProfileHistory;
  settings: UserProfileSettings;
  learnings: UserLearning[];
  created_at: string;
  updated_at: string;
}

// API types for profile
export interface UpdateProfileSettingsRequest {
  settings: Partial<UserProfileSettings>;
}

export interface ExtractLearningsRequest {
  plan_id: string;
}

export interface ExtractLearningsResponse {
  learning: UserLearning;
  updated_patterns: UserProfilePatterns;
}
